<template>
  <div v-if="isVisible && overlayComponent" class="fixed inset-0 z-40 overscroll-none overflow-hidden bg-black/80 w-full transition-opacity duration-300">
    <!-- Close button -->
    <div class="sticky top-3 m-3 mt-12 float-right z-20">
      <button
        @click="closeOverlay"
        type="button"
        class="shadow-md rounded-full text-sm p-1.5 inline-flex items-center"
        :style="{
          backgroundColor: tourSettings?.header_background_contrast_color || '#000',
          color: tourSettings?.header_text_color || '#fff'
        }"
      >
        <svg
          class="w-5 h-5"
          fill="currentColor"
          viewBox="0 0 20 20"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" />
        </svg>
      </button>
    </div>

    <!-- Main content -->
    <div class="absolute mt-8 flex flex-col w-full h-full" style="min-height: calc(var(--vh, 1vh) * 100);">
      <!-- Header with image -->
      <div 
        class="flex flex-col rounded-t-3xl p-4 relative"
        :style="{ 
          backgroundColor: overlayProps.image ? 'transparent' : (tourSettings?.header_background_color || '#fff'),
          color: tourSettings?.header_text_color || '#000'
        }"
      >
        <!-- Background image with gradient overlay -->
        <div v-if="overlayProps.image" class="absolute inset-0 rounded-t-3xl">
          <img 
            :src="overlayProps.image.preview_url" 
            :alt="overlayProps.image.title || overlayProps.image.name"
            class="w-full h-full object-cover rounded-t-3xl"
          />
          <div class="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent rounded-t-3xl"></div>
        </div>
        
        <!-- Header content -->
        <div 
          class="flex flex-col h-full justify-between relative z-10"
          :class="overlayProps.image ? 'min-h-[10rem]' : 'min-h-[2rem]'"
        >
          <div>
            <!-- Meta data like score can go here -->
          </div>
          <div :style="{ color: overlayProps.image ? 'white' : (tourSettings?.header_text_color || '#000') }">
            <p class="text-2xl line-clamp-2 mr-10">{{ overlayTitle }}</p>
            <p v-if="overlayProps.subtitle" class="text-lg line-clamp-2 leading-6">{{ overlayProps.subtitle }}</p>
          </div>
        </div>
      </div>

      <!-- Content area -->
      <div class="flex-grow overflow-y-auto ios-scroll w-full bg-white" style="height: 0; flex: 1 1 0;">
        <component v-if="overlayComponent" :is="overlayComponent" v-bind="overlayProps" />
        <div v-else class="p-4 text-center text-gray-500">
          Loading...
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'

export default {
  name: 'VueOverlay',
  data() {
    return {}
  },
  computed: {
    ...mapGetters('overlay', ['isVisible', 'overlayComponent', 'overlayTitle', 'overlayProps']),
    ...mapGetters('settings', ['tourSettings'])
  },
  watch: {
    isVisible(newVal) {
      if (newVal) {
        // Prevent body scroll when overlay is open
        document.body.classList.add('overlay-open')
        this.addScrollListener()
      } else {
        // Restore body scroll when overlay is closed
        document.body.classList.remove('overlay-open')
        this.removeScrollListener()
      }
    }
  },
  mounted() {
    if (this.isVisible) {
      document.body.classList.add('overlay-open')
      this.addScrollListener()
    }
  },
  beforeUnmount() {
    // Clean up scroll prevention
    document.body.classList.remove('overlay-open')
    this.removeScrollListener()
  },
  methods: {
    ...mapActions('overlay', ['closeOverlay']),
    addScrollListener() {
      this.$nextTick(() => {
        const contentArea = this.$el?.querySelector('.flex-grow.overflow-y-auto')
        if (contentArea) {
          // Prevent touch events from bubbling to parent
          this.touchStartHandler = (e) => e.stopPropagation()
          this.touchMoveHandler = (e) => e.stopPropagation()
          this.touchEndHandler = (e) => e.stopPropagation()
          
          contentArea.addEventListener('touchstart', this.touchStartHandler, { passive: false })
          contentArea.addEventListener('touchmove', this.touchMoveHandler, { passive: false })
          contentArea.addEventListener('touchend', this.touchEndHandler, { passive: false })
        }
      })
    },
    removeScrollListener() {
      if (this.touchStartHandler) {
        const contentArea = this.$el?.querySelector('.flex-grow.overflow-y-auto')
        if (contentArea) {
          contentArea.removeEventListener('touchstart', this.touchStartHandler)
          contentArea.removeEventListener('touchmove', this.touchMoveHandler)
          contentArea.removeEventListener('touchend', this.touchEndHandler)
        }
        this.touchStartHandler = null
        this.touchMoveHandler = null
        this.touchEndHandler = null
      }
    }
  }
}
</script>

<style scoped>
.hide-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.hide-scrollbar::-webkit-scrollbar {
  display: none;
}

/* Prevent parent scrolling when overlay is open */
:global(body.overlay-open) {
  overflow: hidden !important;
  position: fixed !important;
  width: 100% !important;
}
</style> 